import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  Calendar,
  Filter,
  Image as ImageIcon,
  Loader2,
  Plus,
  Search,
  Trash2,
  UploadCloud,
  X,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';
import {
  addTagToImage,
  deleteCarImage,
  getGalleryImages,
  removeTagFromImage,
  updateImageMetadata,
  uploadCarImage,
} from '../lib/carGalleryApi';

type SortMode = 'newest' | 'oldest';
type QuickView = 'all' | 'exterior' | 'interior' | 'engine' | 'wheels' | 'documents';

type CarGalleryImage = {
  id: string;
  file_path: string;
  url: string;
  tags: string[] | null;
  created_at: string;
  title?: string | null;
  description?: string | null;
  taken_at?: string | null;
};

const FALLBACK_IMG = '/fallback-car.png';
const MAX_UPLOAD_SIZE_BYTES = 10 * 1024 * 1024;

const quickViewTagMap: Record<Exclude<QuickView, 'all'>, string[]> = {
  exterior: ['exterior', 'outside', 'body'],
  interior: ['interior', 'inside', 'cabin'],
  engine: ['engine', 'motor'],
  wheels: ['wheels', 'wheel', 'rim', 'rims', 'tire', 'tires'],
  documents: ['documents', 'paperwork', 'registration', 'insurance'],
};

const viewOptions: { value: QuickView; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'exterior', label: 'Exterior' },
  { value: 'interior', label: 'Interior' },
  { value: 'engine', label: 'Engine' },
  { value: 'wheels', label: 'Wheels' },
  { value: 'documents', label: 'Documents' },
];

const normalizeTag = (tag: string) => tag.trim().toLowerCase();

const formatDate = (value: string | null | undefined) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString();
};

const toDateInputValue = (value: string | null | undefined) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
};

type MetadataFormState = {
  title: string;
  description: string;
  takenAt: string;
};

const CarGallery: React.FC = () => {
  const [images, setImages] = useState<CarGalleryImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadStatus, setUploadStatus] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [sortMode, setSortMode] = useState<SortMode>('newest');
  const [quickView, setQuickView] = useState<QuickView>('all');
  const [tagInputs, setTagInputs] = useState<Record<string, string>>({});
  const [editingMetadataPath, setEditingMetadataPath] = useState<string | null>(null);
  const [metadataForms, setMetadataForms] = useState<Record<string, MetadataFormState>>({});
  const [savingMetadataPath, setSavingMetadataPath] = useState<string | null>(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [zoom, setZoom] = useState(1);

  const loadGallery = async (initial = false) => {
    if (initial) setLoading(true);
    else setRefreshing(true);

    setError(null);
    const imagesResult = await getGalleryImages();

    if (imagesResult.error) {
      setError(imagesResult.error?.message || imagesResult.error || 'Failed to load gallery');
    } else {
      setImages((imagesResult.data || []) as CarGalleryImage[]);
    }

    if (initial) setLoading(false);
    else setRefreshing(false);
  };

  useEffect(() => {
    loadGallery(true);
  }, []);

  const sortedImages = useMemo(() => {
    const sorted = [...images];
    sorted.sort((a, b) => {
      const aTime = new Date(a.created_at).getTime();
      const bTime = new Date(b.created_at).getTime();
      return sortMode === 'newest' ? bTime - aTime : aTime - bTime;
    });
    return sorted;
  }, [images, sortMode]);

  const filteredImages = useMemo(() => {
    const searchTerm = search.trim().toLowerCase();
    const quickViewTerms = quickView === 'all' ? [] : quickViewTagMap[quickView];

    return sortedImages.filter((image) => {
      const tags = (image.tags || []).map(normalizeTag);
      const fileName = image.file_path.split('/').pop()?.toLowerCase() || '';
      const title = (image.title || '').toLowerCase();
      const description = (image.description || '').toLowerCase();

      if (quickViewTerms.length > 0) {
        const matchesQuickView = quickViewTerms.some((term) => tags.includes(term));
        if (!matchesQuickView) return false;
      }

      if (!searchTerm) return true;

      return (
        fileName.includes(searchTerm) ||
        title.includes(searchTerm) ||
        description.includes(searchTerm) ||
        tags.some((tag) => tag.includes(searchTerm))
      );
    });
  }, [quickView, search, sortedImages]);

  const galleryStats = useMemo(() => {
    const newest = images.length > 0 ? images.reduce((latest, current) => {
      return new Date(current.created_at).getTime() > new Date(latest.created_at).getTime() ? current : latest;
    }, images[0]) : null;

    return {
      total: images.length,
      shown: filteredImages.length,
      newestDate: newest ? formatDate(newest.created_at) : '-',
    };
  }, [filteredImages.length, images]);

  const activeImage = lightboxOpen ? filteredImages[activeIndex] : null;

  const openLightbox = (index: number) => {
    setActiveIndex(index);
    setZoom(1);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setZoom(1);
  };

  const handlePrev = () => {
    setActiveIndex((prev) => (prev > 0 ? prev - 1 : prev));
    setZoom(1);
  };

  const handleNext = () => {
    setActiveIndex((prev) => (prev < filteredImages.length - 1 ? prev + 1 : prev));
    setZoom(1);
  };

  useEffect(() => {
    if (!lightboxOpen) return undefined;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeLightbox();
      if (event.key === 'ArrowLeft') {
        setActiveIndex((prev) => (prev > 0 ? prev - 1 : prev));
        setZoom(1);
      }
      if (event.key === 'ArrowRight') {
        setActiveIndex((prev) => (prev < filteredImages.length - 1 ? prev + 1 : prev));
        setZoom(1);
      }
      if (event.key === '+') setZoom((value) => Math.min(3, value + 0.25));
      if (event.key === '-') setZoom((value) => Math.max(1, value - 0.25));
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [lightboxOpen, filteredImages.length]);

  const validateUploadFile = (file: File) => {
    if (!file.type.startsWith('image/')) return 'Only image files are allowed';
    if (file.size > MAX_UPLOAD_SIZE_BYTES) return 'Each image must be 10MB or smaller';
    return null;
  };

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const list = event.target.files;
    if (!list || list.length === 0) return;

    const files = Array.from(list);
    setUploading(true);
    setError(null);
    setUploadStatus(null);

    let okCount = 0;
    let failCount = 0;

    for (const file of files) {
      const validationError = validateUploadFile(file);
      if (validationError) {
        failCount += 1;
        setError(validationError);
        continue;
      }

      const { error: uploadError } = await uploadCarImage(file);
      if (uploadError) {
        failCount += 1;
        setError(uploadError?.message || uploadError || 'Upload failed');
      } else {
        okCount += 1;
      }
    }

    await loadGallery();
    setUploadStatus(`Uploaded ${okCount} file(s), failed ${failCount}`);
    setUploading(false);
    event.target.value = '';
  };

  const handleDelete = async (path: string) => {
    const shouldDelete = window.confirm('Delete this photo?');
    if (!shouldDelete) return;

    setRefreshing(true);
    setError(null);
    const { error: deleteError } = await deleteCarImage(path);
    if (deleteError) {
      setError(deleteError?.message || deleteError || 'Delete failed');
    } else {
      if (activeImage?.file_path === path) closeLightbox();
      await loadGallery();
    }
    setRefreshing(false);
  };

  const handleTagInputChange = (filePath: string, value: string) => {
    setTagInputs((current) => ({ ...current, [filePath]: value }));
  };

  const handleAddTag = async (filePath: string) => {
    const tag = (tagInputs[filePath] || '').trim();
    if (!tag) return;

    setRefreshing(true);
    setError(null);
    const { error: addError } = await addTagToImage(filePath, tag);
    if (addError) setError(addError?.message || addError || 'Failed to add tag');
    setTagInputs((current) => ({ ...current, [filePath]: '' }));
    await loadGallery();
    setRefreshing(false);
  };

  const handleRemoveTag = async (filePath: string, tag: string) => {
    setRefreshing(true);
    setError(null);
    const { error: removeError } = await removeTagFromImage(filePath, tag);
    if (removeError) setError(removeError?.message || removeError || 'Failed to remove tag');
    await loadGallery();
    setRefreshing(false);
  };

  const startMetadataEdit = (image: CarGalleryImage) => {
    setEditingMetadataPath(image.file_path);
    setMetadataForms((current) => ({
      ...current,
      [image.file_path]: {
        title: image.title || '',
        description: image.description || '',
        takenAt: toDateInputValue(image.taken_at),
      },
    }));
  };

  const cancelMetadataEdit = () => {
    setEditingMetadataPath(null);
  };

  const handleMetadataInput = (
    filePath: string,
    field: keyof MetadataFormState,
    value: string
  ) => {
    setMetadataForms((current) => ({
      ...current,
      [filePath]: {
        ...(current[filePath] || { title: '', description: '', takenAt: '' }),
        [field]: value,
      },
    }));
  };

  const handleSaveMetadata = async (filePath: string) => {
    const form = metadataForms[filePath] || { title: '', description: '', takenAt: '' };
    setSavingMetadataPath(filePath);
    setError(null);

    const { error: updateError } = await updateImageMetadata(filePath, {
      title: form.title,
      description: form.description,
      takenAt: form.takenAt ? new Date(`${form.takenAt}T12:00:00.000Z`).toISOString() : null,
    });

    if (updateError) {
      setError(updateError?.message || updateError || 'Failed to save photo details');
    } else {
      setEditingMetadataPath(null);
      await loadGallery();
    }

    setSavingMetadataPath(null);
  };

  return (
    <div className="min-h-screen bg-theme-secondary pb-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="sticky top-0 z-10 rounded-xl border border-theme-primary bg-theme-primary/95 backdrop-blur p-4 mb-5">
          <div className="flex flex-col gap-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h1 className="text-2xl font-bold text-theme-primary">Car Gallery</h1>
                <p className="text-sm text-theme-secondary">A cleaner timeline of your car photos.</p>
              </div>
              <label className="inline-flex items-center gap-2 rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white cursor-pointer hover:bg-primary-700 transition-colors">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />}
                Upload photos
                <input type="file" accept="image/*" multiple className="hidden" onChange={handleUpload} />
              </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <label className="md:col-span-2 flex items-center gap-2 rounded-md border border-theme-primary bg-theme-secondary px-3 py-2">
                <Search className="h-4 w-4 text-theme-secondary" />
                <input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search by file name, title, description, or tag"
                  className="w-full bg-transparent text-sm text-theme-primary outline-none placeholder:text-theme-tertiary"
                />
              </label>

              <select
                value={sortMode}
                onChange={(event) => setSortMode(event.target.value as SortMode)}
                className="rounded-md border border-theme-primary bg-theme-secondary px-3 py-2 text-sm text-theme-primary"
              >
                <option value="newest">Sort: Newest first</option>
                <option value="oldest">Sort: Oldest first</option>
              </select>

              <button
                type="button"
                onClick={() => {
                  setSearch('');
                  setSortMode('newest');
                  setQuickView('all');
                }}
                className="rounded-md border border-theme-primary px-3 py-2 text-sm text-theme-secondary hover:bg-theme-secondary/10"
              >
                Reset filters
              </button>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Filter className="h-4 w-4 text-theme-secondary" />
              {viewOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setQuickView(option.value)}
                  className={`rounded-full px-3 py-1 text-xs font-semibold border transition-colors ${
                    quickView === option.value
                      ? 'bg-primary-600 border-primary-600 text-white'
                      : 'bg-theme-secondary border-theme-primary text-theme-secondary hover:bg-primary-600 hover:text-white'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>

          </div>
        </div>

        <div className="mb-4 grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
          <div className="rounded-lg border border-theme-primary bg-theme-primary p-3 text-theme-secondary">
            Total photos: <span className="font-semibold text-theme-primary">{galleryStats.total}</span>
          </div>
          <div className="rounded-lg border border-theme-primary bg-theme-primary p-3 text-theme-secondary">
            Showing: <span className="font-semibold text-theme-primary">{galleryStats.shown}</span>
          </div>
          <div className="rounded-lg border border-theme-primary bg-theme-primary p-3 text-theme-secondary">
            Newest upload: <span className="font-semibold text-theme-primary">{galleryStats.newestDate}</span>
          </div>
        </div>

        {uploadStatus && (
          <p className="mb-3 rounded-md border border-theme-primary bg-theme-primary px-3 py-2 text-sm text-theme-secondary">
            {uploadStatus}
          </p>
        )}
        {error && (
          <p className="mb-3 rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-400">{error}</p>
        )}

        {(loading || refreshing || uploading) && (
          <div className="mb-4 flex items-center gap-2 text-theme-secondary">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-sm">{loading ? 'Loading gallery...' : 'Syncing gallery...'}</span>
          </div>
        )}

        {!loading && filteredImages.length === 0 ? (
          <div className="rounded-xl border border-theme-primary bg-theme-primary p-10 text-center">
            <ImageIcon className="mx-auto mb-3 h-12 w-12 text-theme-tertiary" />
            <h2 className="mb-1 text-lg font-semibold text-theme-primary">No photos found</h2>
            <p className="text-sm text-theme-secondary">
              Upload your first car photo or adjust filters to see more results.
            </p>
          </div>
        ) : (
          <div className="columns-2 md:columns-3 lg:columns-4 gap-4 [column-fill:_balance]">
            {filteredImages.map((image, index) => (
              <article
                key={image.file_path}
                className="mb-4 break-inside-avoid rounded-xl border border-theme-primary bg-theme-primary overflow-hidden shadow-sm"
              >
                <button
                  type="button"
                  onClick={() => openLightbox(index)}
                  className="group relative block w-full overflow-hidden"
                  title="Open photo"
                >
                  <img
                    src={image.url}
                    alt={image.title || 'Car photo'}
                    className="w-full object-cover transition-transform duration-300 group-hover:scale-105"
                    loading="lazy"
                    onError={(event) => {
                      (event.target as HTMLImageElement).src = FALLBACK_IMG;
                    }}
                  />
                  <span className="absolute left-2 top-2 rounded-full bg-black/50 px-2 py-0.5 text-xs text-white">
                    {index + 1}
                  </span>
                </button>

                <div className="p-3 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="text-sm font-semibold text-theme-primary">
                        {image.title || image.file_path.split('/').pop()}
                      </p>
                      <p className="text-xs text-theme-secondary inline-flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {formatDate(image.taken_at || image.created_at)}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleDelete(image.file_path)}
                      className="rounded-md p-1.5 text-theme-secondary hover:bg-red-500/10 hover:text-red-400"
                      title="Delete photo"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>

                  {image.description && (
                    <p className="text-xs text-theme-secondary line-clamp-2">{image.description}</p>
                  )}

                  {editingMetadataPath === image.file_path ? (
                    <form
                      className="space-y-2 rounded-md border border-theme-primary bg-theme-secondary/40 p-2"
                      onSubmit={(event) => {
                        event.preventDefault();
                        handleSaveMetadata(image.file_path);
                      }}
                    >
                      <input
                        type="text"
                        value={metadataForms[image.file_path]?.title || ''}
                        onChange={(event) => handleMetadataInput(image.file_path, 'title', event.target.value)}
                        placeholder="Title"
                        className="w-full rounded-md border border-theme-primary bg-theme-secondary px-2 py-1 text-xs text-theme-primary outline-none focus:ring-2 focus:ring-primary-500"
                      />
                      <textarea
                        value={metadataForms[image.file_path]?.description || ''}
                        onChange={(event) => handleMetadataInput(image.file_path, 'description', event.target.value)}
                        placeholder="Description"
                        rows={2}
                        className="w-full rounded-md border border-theme-primary bg-theme-secondary px-2 py-1 text-xs text-theme-primary outline-none focus:ring-2 focus:ring-primary-500"
                      />
                      <input
                        type="date"
                        value={metadataForms[image.file_path]?.takenAt || ''}
                        onChange={(event) => handleMetadataInput(image.file_path, 'takenAt', event.target.value)}
                        className="w-full rounded-md border border-theme-primary bg-theme-secondary px-2 py-1 text-xs text-theme-primary outline-none focus:ring-2 focus:ring-primary-500"
                      />
                      <div className="flex items-center gap-2">
                        <button
                          type="submit"
                          disabled={savingMetadataPath === image.file_path}
                          className="rounded-md bg-primary-600 px-2 py-1 text-xs text-white hover:bg-primary-700 disabled:opacity-60"
                        >
                          {savingMetadataPath === image.file_path ? 'Saving...' : 'Save details'}
                        </button>
                        <button
                          type="button"
                          onClick={cancelMetadataEdit}
                          className="rounded-md border border-theme-primary px-2 py-1 text-xs text-theme-secondary hover:bg-theme-secondary/10"
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  ) : (
                    <button
                      type="button"
                      onClick={() => startMetadataEdit(image)}
                      className="rounded-md border border-theme-primary px-2 py-1 text-xs text-theme-secondary hover:bg-theme-secondary/10"
                    >
                      Edit details
                    </button>
                  )}

                  <div className="flex flex-wrap gap-1">
                    {(image.tags || []).map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex items-center gap-1 rounded-full bg-primary-600/90 px-2 py-0.5 text-[11px] text-white"
                      >
                        {tag}
                        <button
                          type="button"
                          onClick={() => handleRemoveTag(image.file_path, tag)}
                          className="hover:text-red-200"
                          title="Remove tag"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>

                  <form
                    className="flex items-center gap-2"
                    onSubmit={(event) => {
                      event.preventDefault();
                      handleAddTag(image.file_path);
                    }}
                  >
                    <input
                      type="text"
                      value={tagInputs[image.file_path] || ''}
                      onChange={(event) => handleTagInputChange(image.file_path, event.target.value)}
                      placeholder="Add tag"
                      maxLength={20}
                      className="w-full rounded-md border border-theme-primary bg-theme-secondary px-2 py-1 text-xs text-theme-primary outline-none focus:ring-2 focus:ring-primary-500"
                    />
                    <button
                      type="submit"
                      className="inline-flex items-center rounded-md bg-primary-600 p-1.5 text-white hover:bg-primary-700"
                      title="Add tag"
                    >
                      <Plus className="h-3.5 w-3.5" />
                    </button>
                  </form>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>

      {lightboxOpen && activeImage && (
        <div className="fixed inset-0 z-50 bg-black/90 p-4">
          <div className="mx-auto flex h-full max-w-6xl flex-col">
            <div className="mb-3 flex items-center justify-between text-white">
              <p className="text-sm">
                {activeIndex + 1} / {filteredImages.length}
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setZoom((value) => Math.max(1, value - 0.25))}
                  className="rounded-md bg-white/10 p-2 hover:bg-white/20 disabled:opacity-40"
                  disabled={zoom <= 1}
                >
                  <ZoomOut className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setZoom((value) => Math.min(3, value + 0.25))}
                  className="rounded-md bg-white/10 p-2 hover:bg-white/20 disabled:opacity-40"
                  disabled={zoom >= 3}
                >
                  <ZoomIn className="h-4 w-4" />
                </button>
                <button type="button" onClick={closeLightbox} className="rounded-md bg-white/10 p-2 hover:bg-white/20">
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="relative flex-1 overflow-hidden rounded-xl border border-white/20">
              <img
                src={activeImage.url}
                alt={activeImage.title || 'Car photo'}
                className="h-full w-full object-contain select-none"
                style={{ transform: `scale(${zoom})`, transition: 'transform 0.15s ease-out' }}
                onError={(event) => {
                  (event.target as HTMLImageElement).src = FALLBACK_IMG;
                }}
              />

              <button
                type="button"
                onClick={handlePrev}
                disabled={activeIndex === 0}
                className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-white/15 p-2 text-white hover:bg-white/25 disabled:opacity-40"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <button
                type="button"
                onClick={handleNext}
                disabled={activeIndex === filteredImages.length - 1}
                className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-white/15 p-2 text-white hover:bg-white/25 disabled:opacity-40"
              >
                <ArrowRight className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CarGallery;